/**
 * Risk Trend Bar Component
 *
 * Displays a 14-day risk trend as colored segments below the RiskLevelBadge.
 * - Width: 130px (same as RiskLevelBadge)
 * - Height: 8px (25% of 32px badge height)
 * - 14 equal segments with risk-level-appropriate colors
 */

import { RiskLevel } from "../../types/residents";

interface RiskTrendBarProps {
  trend: (RiskLevel | null)[];
  className?: string;
}

function getSegmentColor(level: RiskLevel | null): string {
  switch (level) {
    case "HIGH":
      return "#B02828"; // Red
    case "MEDIUM":
      return "#D97706"; // Orange/amber
    case "LOW":
      return "#15803D"; // Green
    case null:
    default:
      return "#E5E7EB"; // Light gray for no data
  }
}

/**
 * Builds a CSS linear gradient with smooth transitions between adjacent segments.
 * Uses overlapping color stops to create subtle blending while keeping segments distinguishable.
 */
function buildGradient(trend: (RiskLevel | null)[]): string {
  const stops: string[] = [];
  const segmentWidth = 100 / trend.length;
  const blendAmount = 1.5; // Subtle blend - segments distinguishable but smooth

  trend.forEach((level, i) => {
    const color = getSegmentColor(level);
    const start = i * segmentWidth;
    const end = (i + 1) * segmentWidth;

    if (i === 0) {
      // First segment: solid from start, blend at end
      stops.push(`${color} ${end - blendAmount}%`);
    } else if (i === trend.length - 1) {
      // Last segment: blend at start, solid to end
      stops.push(`${color} ${start + blendAmount}%`);
    } else {
      // Middle segments: blend on both ends
      stops.push(`${color} ${start + blendAmount}%`);
      stops.push(`${color} ${end - blendAmount}%`);
    }
  });

  return `linear-gradient(to right, ${stops.join(", ")})`;
}

export function RiskTrendBar({ trend, className = "" }: RiskTrendBarProps) {
  // Don't render if trend array is empty
  if (!trend || trend.length === 0) {
    return null;
  }

  // Ensure we have exactly 14 segments (pad with null if needed)
  const normalizedTrend = [...trend];
  while (normalizedTrend.length < 14) {
    normalizedTrend.unshift(null);
  }
  const displayTrend = normalizedTrend.slice(-14);

  return (
    <div
      className={`w-[130px] h-2 rounded-sm overflow-hidden ${className}`}
      style={{ background: buildGradient(displayTrend) }}
      title="14-day risk trend (oldest to newest)"
    />
  );
}
